// libs/sdk/src/lib/swap/swap-context.ts
import {
  defer,
  distinctUntilChanged as distinctUntilChanged2,
  map as map2,
  merge,
  Subscription as Subscription2,
  switchMap as switchMap2,
  tap as tap2,
  combineLatest,
  withLatestFrom,
  debounceTime,
  of,
  startWith as startWith3,
  shareReplay as shareReplay2,
  BehaviorSubject,
  firstValueFrom,
  Subject as Subject3,
  subscribeOn,
  asyncScheduler
} from "rxjs";

// libs/sdk/src/lib/swap/token-context.ts
import { distinctUntilChanged, map, shareReplay, startWith, Subject } from "rxjs";
import { isTokensEqual } from "@one-inch-community/sdk/tokens";
var TokenContext = class {
  constructor() {
    this.lastSnapshot = { token: null, amount: null };
    this.signalChange$ = new Subject();
  }
  setToken(token) {
    const { token: tokenFromSnap } = this.lastSnapshot;
    if (tokenFromSnap && token && isTokensEqual(tokenFromSnap, token)) {
      return;
    }
    this.lastSnapshot = { ...this.lastSnapshot, token, amount: 0n };
    this.signalChange$.next();
  }
  setAmount(amount) {
    const { amount: amountFromSnap } = this.lastSnapshot;
    if (amountFromSnap === amount) {
      return;
    }
    this.lastSnapshot = { ...this.lastSnapshot, amount };
    this.signalChange$.next();
  }
  getSnapshot() {
    return this.lastSnapshot;
  }
  streamSnapshot() {
    return this.signalChange$.pipe(
      map(() => this.lastSnapshot),
      startWith(this.lastSnapshot),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
};

// libs/sdk/src/lib/swap/pair-holder.ts
import { startWith as startWith2, Subject as Subject2, Subscription, switchMap } from "rxjs";
import { JsonParser } from "@one-inch-community/core/storage";

// libs/sdk/src/lib/chain/is-chain-id.ts
import { ChainId } from "@one-inch-community/models";
function isChainId(chainId) {
  return ChainId[chainId] !== void 0;
}

// libs/sdk/src/lib/tokens/is-tokens-equal.ts
import { isAddressEqual } from "viem";
function isTokensEqual2(token1, token2) {
  const isAddressMatching = isAddressEqual(token1.address, token2.address);
  const isChainIdMatching = token1.chainId === token2.chainId;
  return isAddressMatching && isChainIdMatching;
}

// libs/sdk/src/lib/swap/pair-holder.ts
var PairHolder = class {
  constructor(applicationContext) {
    this.applicationContext = applicationContext;
    this.subscription = new Subscription();
    this.source = new TokenContext();
    this.destination = new TokenContext();
    this.pairChance$ = new Subject2();
  }
  init() {
    this.restorePair();
    this.startChainTokenSync();
  }
  restorePair() {
    this.restoreToken("source");
    this.restoreToken("destination");
  }
  switchPair() {
    const { source, destination } = this;
    this.source = destination;
    this.destination = source;
    this.updateTokenStore("source");
    this.updateTokenStore("destination");
    this.pairChance$.next();
  }
  setPair(pair) {
    this.setTokenInner(pair.source, "source");
    this.setTokenInner(pair.destination, "destination");
    this.pairChance$.next();
  }
  setToken(token, tokenType) {
    if (tokenType === "source" && token !== null) {
      const destinationTokenSnap = this.destination.getSnapshot();
      if (destinationTokenSnap.token !== null && isTokensEqual2(token, destinationTokenSnap.token)) {
        return this.switchPair();
      }
    }
    if (tokenType === "destination" && token !== null) {
      const sourceTokenSnap = this.source.getSnapshot();
      if (sourceTokenSnap.token !== null && isTokensEqual2(token, sourceTokenSnap.token)) {
        return this.switchPair();
      }
    }
    this.setTokenInner(token, tokenType);
  }
  setAmount(tokenType, value) {
    this.getTokenContext(tokenType).setAmount(value);
    this.updateTokenStore(tokenType);
  }
  streamSnapshot(tokenType) {
    return this.pairChance$.pipe(
      startWith2(null),
      switchMap(() => this.getTokenContext(tokenType).streamSnapshot())
    );
  }
  getSnapshot(tokenType) {
    return this.getTokenContext(tokenType).getSnapshot();
  }
  destroy() {
    this.subscription.unsubscribe();
  }
  setTokenInner(token, tokenType) {
    this.getTokenContext(tokenType).setToken(token);
    this.updateTokenStore(tokenType);
  }
  getTokenContext(tokenType) {
    if (tokenType === "source") {
      return this.source;
    }
    if (tokenType === "destination") {
      return this.destination;
    }
    throw new Error(`invalid token type ${tokenType}`);
  }
  updateTokenStore(tokenType) {
    const tokenContext = this.getTokenContext(tokenType);
    const snapshot = tokenContext.getSnapshot();
    const amount = snapshot.amount;
    if (amount !== null) {
      this.applicationContext.storageController.set(
        this.tokenStoreKey(tokenType) + "_amount",
        amount.toString()
      );
    }
    if (snapshot.token !== null) {
      const { symbol, address, chainId, name, decimals } = snapshot.token;
      this.applicationContext.storageController.set(
        this.tokenStoreKey(tokenType),
        { symbol, address, chainId, name, decimals }
      );
    }
  }
  restoreToken(tokenType) {
    const tokenContext = this.getTokenContext(tokenType);
    const amount = this.applicationContext.storageController.get(this.tokenStoreKey(tokenType) + "_amount", String);
    const token = this.applicationContext.storageController.get(this.tokenStoreKey(tokenType), JsonParser);
    if (token !== null) {
      tokenContext.setToken(token);
    }
    if (amount !== null) {
      tokenContext.setAmount(BigInt(amount));
    }
  }
  tokenStoreKey(tokenType) {
    return `token_${tokenType}`;
  }
  startChainTokenSync() {
    this.subscription.add(this.applicationContext.connectWalletController.data.chainId$.pipe(
      switchMap(async (chainId) => {
        if (!chainId || !isChainId(chainId)) return;
        const sourceTokenSnap = this.source.getSnapshot();
        const destinationTokenSnap = this.destination.getSnapshot();
        if (sourceTokenSnap.token && sourceTokenSnap.token.chainId !== chainId) {
          const token = await this.applicationContext.tokenController.getNativeToken(chainId);
          this.setTokenInner(token, "source");
        }
        if (destinationTokenSnap.token && destinationTokenSnap.token.chainId !== chainId) {
          this.setTokenInner(null, "destination");
        }
      })
    ).subscribe());
  }
};

// libs/sdk/src/lib/swap/swap-context-onchain.strategy.ts
import { BigMath } from "@one-inch-community/core/math";
var SwapContextOnChainStrategy = class {
  constructor(pairHolder, walletController, rateProvider) {
    this.pairHolder = pairHolder;
    this.walletController = walletController;
    this.rateProvider = rateProvider;
  }
  swap(swapSnapshot) {
    throw new Error("OnChain strategy not support swap");
  }
  async getDataSnapshot() {
    const sourceTokenSnapshot = this.pairHolder.getSnapshot("source");
    const destinationTokenSnapshot = this.pairHolder.getSnapshot("destination");
    const { token: sourceToken, amount: sourceTokenAmount } = sourceTokenSnapshot;
    const { token: destinationToken } = destinationTokenSnapshot;
    const chainId = await this.walletController.data.getChainId();
    if (chainId === null || sourceToken === null || destinationToken === null || sourceTokenAmount === null) {
      throw new Error("");
    }
    const rate = await this.rateProvider.getOnChainRate(
      chainId,
      sourceToken,
      destinationToken
    );
    if (rate === null) {
      throw new Error("");
    }
    let destinationTokenAmount;
    if (rate.isReverted) {
      destinationTokenAmount = BigMath.div(
        sourceTokenAmount,
        rate.revertedRate,
        sourceToken.decimals,
        sourceToken.decimals,
        destinationToken.decimals
      );
    } else {
      destinationTokenAmount = BigMath.mul(
        sourceTokenAmount,
        rate.rate,
        sourceToken.decimals,
        destinationToken.decimals,
        destinationToken.decimals
      );
    }
    return {
      chainId,
      sourceToken,
      destinationToken,
      sourceTokenAmount,
      destinationTokenAmount,
      rate,
      minReceive: destinationTokenAmount,
      autoAuctionTime: null,
      autoSlippage: null,
      rawResponseData: null
    };
  }
};

// libs/sdk/src/lib/swap/swap-context-fusion.strategy.ts
import {
  getOneInchRouterV6ContractAddress,
  getPermit,
  getWrapperNativeToken,
  isNativeToken,
  preparePermit2ForSwap
} from "@one-inch-community/sdk/chain";
import { BigMath as BigMath2 } from "@one-inch-community/core/math";
import { buildFusionSdk } from "@one-inch-community/sdk/api";
var RATE_BUMP_DENOMINATOR = 10000000n;
var SwapContextFusionStrategy = class {
  constructor(swapContext, pairHolder, walletController, settings, devPortalAdapter) {
    this.swapContext = swapContext;
    this.pairHolder = pairHolder;
    this.walletController = walletController;
    this.settings = settings;
    this.devPortalAdapter = devPortalAdapter;
  }
  async swap(swapSnapshot) {
    const {
      chainId,
      sourceToken,
      destinationToken,
      sourceTokenAmount,
      destinationTokenAmount,
      slippage,
      auctionTime,
      rawResponseData
    } = swapSnapshot;
    const walletAddress = await this.walletController.data.getActiveAddress();
    if (walletAddress === null) {
      throw new Error("Wallet not connected");
    }
    if (!rawResponseData) {
      throw new Error("");
    }
    const fusionSDK = await buildFusionSdk(chainId, this.walletController);
    const permitData = await getPermit(chainId, sourceToken.address, walletAddress, getOneInchRouterV6ContractAddress(chainId));
    const orderParams = {
      walletAddress,
      fromTokenAddress: sourceToken.address,
      toTokenAddress: destinationToken.address,
      amount: sourceTokenAmount.toString(),
      preset: rawResponseData.recommended_preset
    };
    if (permitData) {
      orderParams.permit = await preparePermit2ForSwap(chainId, walletAddress, permitData.signature, permitData.permitSingle);
      orderParams.isPermit2 = true;
    }
    const { type: slippageType, value: slippageValue } = slippage;
    const { type: auctionTimeType, value: auctionTimeValue } = auctionTime;
    if (slippageType !== "auto" || auctionTimeType !== "auto") {
      const preset = rawResponseData.presets[rawResponseData.recommended_preset];
      const auctionEndAmount = slippageType !== "auto" ? destinationTokenAmount - BigMath2.calculatePercentage(destinationTokenAmount, slippageValue ?? rawResponseData.autoK) : BigInt(preset.auctionEndAmount);
      const auctionDuration = auctionTimeType !== "auto" ? auctionTimeValue ?? preset.auctionDuration : preset.auctionDuration;
      const auctionStartAmount = preset.auctionStartAmount;
      const getToTokenAmount = (coefficient) => {
        let rate = coefficient - preset.gasCost.gasBumpEstimate;
        if (rate < 0) rate = 0;
        return auctionEndAmount * (BigInt(rate) + RATE_BUMP_DENOMINATOR) / RATE_BUMP_DENOMINATOR;
      };
      const getDelay = (delay) => {
        if (auctionTimeType === "auto") return delay;
        return delay * auctionTimeValue / preset.auctionDuration;
      };
      orderParams.customPreset = {
        auctionDuration,
        auctionStartAmount: BigInt(auctionStartAmount).toString(),
        auctionEndAmount: auctionEndAmount.toString()
        // points: [
        //   ...preset.points.map((point, index) => ({
        //       delay: getDelay(point.delay),
        //       toTokenAmount: getToTokenAmount(point.coefficient).toString()
        //   }))
        // ]
      };
      orderParams.preset = "custom";
    }
    const createOrderResponse = await fusionSDK.createOrder(orderParams);
    const info = await fusionSDK.submitOrder(createOrderResponse.order, createOrderResponse.quoteId);
    return info.orderHash;
  }
  async getDataSnapshot() {
    const sourceTokenSnapshot = this.pairHolder.getSnapshot("source");
    const destinationTokenSnapshot = this.pairHolder.getSnapshot("destination");
    let { token: sourceToken } = sourceTokenSnapshot;
    const { amount: sourceTokenAmount } = sourceTokenSnapshot;
    const { token: destinationToken } = destinationTokenSnapshot;
    const chainId = await this.walletController.data.getChainId();
    const activeAddress = await this.walletController.data.getActiveAddress();
    if (chainId === null || sourceToken === null || destinationToken === null || sourceTokenAmount === null || activeAddress === null || sourceTokenAmount === 0n) {
      throw new Error("");
    }
    const balance = await this.swapContext.getMaxAmount();
    if (balance < sourceTokenAmount) {
      throw new Error("");
    }
    if (isNativeToken(sourceToken.address)) {
      sourceToken = getWrapperNativeToken(chainId);
    }
    const fusionQuoteReceive = await this.devPortalAdapter.getFusionQuoteReceive(
      chainId,
      sourceToken.address,
      destinationToken.address,
      sourceTokenAmount,
      activeAddress
    );
    if (fusionQuoteReceive === null) {
      throw new Error("");
    }
    const { toTokenAmount, recommended_preset, presets, autoK } = fusionQuoteReceive;
    const recommendedPreset = presets[recommended_preset];
    const marketPrice = BigInt(toTokenAmount);
    const rate = BigMath2.div(
      marketPrice,
      sourceTokenAmount,
      destinationToken.decimals,
      sourceToken.decimals
    );
    const revertedRate = BigMath2.div(
      sourceTokenAmount,
      marketPrice,
      sourceToken.decimals,
      destinationToken.decimals
    );
    const rateData = {
      chainId,
      rate,
      revertedRate,
      isReverted: false,
      sourceToken,
      destinationToken
    };
    const slippageSettings = this.settings.slippage;
    let minReceive = BigInt(recommendedPreset.auctionEndAmount);
    if (slippageSettings.value !== null) {
      const [slippage] = slippageSettings.value;
      const percentAmount = BigMath2.calculatePercentage(marketPrice, slippage);
      minReceive = marketPrice - percentAmount;
    }
    return {
      chainId,
      sourceToken,
      destinationToken,
      sourceTokenAmount,
      minReceive,
      destinationTokenAmount: marketPrice,
      autoAuctionTime: recommendedPreset.auctionDuration,
      autoSlippage: autoK,
      rate: rateData,
      rawResponseData: fusionQuoteReceive
    };
  }
};

// libs/sdk/src/lib/swap/swap-context.ts
import {
  estimateWrap,
  getApproveSimulation,
  getBlockEmitter,
  getOneInchRouterV6ContractAddress as getOneInchRouterV6ContractAddress2,
  getPermit as getPermit2,
  getPermit2TypeData,
  isNativeToken as isNativeToken2,
  savePermit
} from "@one-inch-community/sdk/chain";
import { SettingsController } from "@one-inch-community/core/settings";
import { maxUint256, isAddress } from "viem";
var SwapContext = class {
  constructor(applicationContext) {
    this.applicationContext = applicationContext;
    this.subscription = new Subscription2();
    this.settings = {
      slippage: new SettingsController("slippage"),
      auctionTime: new SettingsController("auctionTime")
    };
    this.loading$ = new BehaviorSubject(false);
    this.chainId$ = defer(() => this.walletController.data.chainId$).pipe(
      distinctUntilChanged2()
    );
    this.connectedWalletAddress$ = defer(() => this.walletController.data.activeAddress$).pipe(
      distinctUntilChanged2()
    );
    this.block$ = this.chainId$.pipe(
      switchMap2((chainId) => chainId ? getBlockEmitter(chainId) : of(null))
    );
    this.updateData$ = new Subject3();
    this.updateDataComplete$ = new Subject3();
    this.dataUpdateEmitter$ = merge(
      this.block$,
      this.chainId$,
      this.connectedWalletAddress$,
      defer(() => this.pairHolder.streamSnapshot("source")),
      defer(() => this.pairHolder.streamSnapshot("destination")),
      this.updateData$
    ).pipe(
      debounceTime(500),
      map2(() => void 0),
      startWith3(void 0),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.dataSnapshot$ = this.dataUpdateEmitter$.pipe(
      withLatestFrom(this.connectedWalletAddress$),
      switchMap2(([_, address]) => {
        this.loading$.next(true);
        return this.getDataSnapshot(address === null);
      }),
      tap2(() => {
        this.loading$.next(false);
        this.updateDataComplete$.next();
      }),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.rate$ = this.dataSnapshot$.pipe(
      map2((snapshot) => snapshot?.rate ?? null)
    );
    this.minReceive$ = this.dataSnapshot$.pipe(
      map2((snapshot) => snapshot?.minReceive ?? 0n)
    );
    this.destinationTokenAmount$ = this.dataSnapshot$.pipe(
      map2((snapshot) => snapshot?.destinationTokenAmount ?? 0n)
    );
    this.autoSlippage$ = this.dataSnapshot$.pipe(
      map2((snapshot) => snapshot?.autoSlippage ?? null)
    );
    this.autoAuctionTime$ = this.dataSnapshot$.pipe(
      map2((snapshot) => snapshot?.autoAuctionTime ?? null)
    );
    this.slippage$ = combineLatest([
      this.autoSlippage$,
      this.settings.slippage.value$
    ]).pipe(
      map2(([autoSlippage, slippageSettings]) => {
        if (slippageSettings) return { type: slippageSettings[1], value: slippageSettings[0] };
        return { type: "auto", value: autoSlippage };
      }),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.auctionTime$ = combineLatest([
      this.autoAuctionTime$,
      this.settings.auctionTime.value$
    ]).pipe(
      map2(([autoAuctionTime, auctionTimeSettings]) => {
        if (auctionTimeSettings) return { type: auctionTimeSettings[1], value: auctionTimeSettings[0] };
        return { type: "auto", value: autoAuctionTime };
      }),
      shareReplay2({ bufferSize: 1, refCount: true })
    );
    this.pairHolder = new PairHolder(this.applicationContext);
    this.oneInchApiAdapter = this.applicationContext.oneInchDevPortalAdapter;
    this.walletController = this.applicationContext.connectWalletController;
    this.contextStrategy = {
      onChain: new SwapContextOnChainStrategy(this.pairHolder, this.walletController, this.applicationContext.tokenRateProvider),
      fusion: new SwapContextFusionStrategy(this, this.pairHolder, this.walletController, this.settings, this.oneInchApiAdapter)
    };
  }
  init() {
    this.pairHolder.init();
    this.subscription.add(
      merge(
        this.destinationTokenAmount$.pipe(
          distinctUntilChanged2(),
          tap2((amount) => {
            this.setTokenAmountByType("destination", amount);
          }),
          subscribeOn(asyncScheduler)
        )
      ).subscribe()
    );
  }
  wrapNativeToken(amount) {
    throw new Error("Method not implemented.");
  }
  async getApprove() {
    const chainId = await this.applicationContext.connectWalletController.data.getChainId();
    const sourceTokenSnapshot = this.pairHolder.getSnapshot("source");
    const owner = await this.applicationContext.connectWalletController.data.getActiveAddress();
    if (!chainId || !sourceTokenSnapshot || !sourceTokenSnapshot.token || !owner || !isAddress(owner)) throw new Error("Invalid approval data");
    const spender = getOneInchRouterV6ContractAddress2(chainId);
    const result = await getApproveSimulation(chainId, sourceTokenSnapshot.token.address, owner, spender, maxUint256);
    return await this.applicationContext.connectWalletController.writeContract(result.request);
  }
  async getApproveForSpender(spender) {
    const chainId = await this.applicationContext.connectWalletController.data.getChainId();
    const sourceTokenSnapshot = this.pairHolder.getSnapshot("source");
    const owner = await this.applicationContext.connectWalletController.data.getActiveAddress();
    if (!chainId || !sourceTokenSnapshot || !sourceTokenSnapshot.token || !owner || !isAddress(owner) || !isAddress(spender)) throw new Error("Missing or invalid data for approval");
    const result = await getApproveSimulation(chainId, sourceTokenSnapshot.token.address, owner, spender, maxUint256);
    return await this.applicationContext.connectWalletController.writeContract(result.request);
  }
  getSettingsController(name) {
    const controller = this.settings[name];
    if (!controller) throw new Error("Settings controller not found");
    return controller;
  }
  async getPermit() {
    const chainId = await this.walletController.data.getChainId();
    const walletAddress = await this.walletController.data.getActiveAddress();
    const tokenSnapshot = this.pairHolder.getSnapshot("source");
    if (!chainId || !walletAddress || !isTokenSnapshotNotNullable(tokenSnapshot) || !isAddress(walletAddress)) {
      throw new Error("Invalid permit data");
    }
    const signFromStorage = await getPermit2(
      chainId,
      tokenSnapshot.token.address,
      walletAddress,
      getOneInchRouterV6ContractAddress2(chainId)
    );
    if (signFromStorage !== null) return;
    const typeData = await getPermit2TypeData(
      chainId,
      tokenSnapshot.token.address,
      walletAddress,
      getOneInchRouterV6ContractAddress2(chainId)
    );
    const sign = await this.walletController.signTypedData(typeData);
    await savePermit(
      chainId,
      tokenSnapshot.token.address,
      walletAddress,
      getOneInchRouterV6ContractAddress2(chainId),
      sign,
      typeData.message
    );
  }
  destroy() {
    this.pairHolder.destroy();
    this.subscription.unsubscribe();
  }
  setPair(pair) {
    this.pairHolder.setPair(pair);
  }
  setToken(tokenType, token) {
    this.pairHolder.setToken(token, tokenType);
  }
  switchPair() {
    this.pairHolder.switchPair();
  }
  async getSnapshot() {
    this.updateData$.next();
    await firstValueFrom(this.updateDataComplete$);
    const [swapSnapshot, slippage, auctionTime] = await Promise.all([
      firstValueFrom(this.dataSnapshot$),
      firstValueFrom(this.slippage$),
      firstValueFrom(this.auctionTime$)
    ]);
    return {
      ...swapSnapshot,
      slippage,
      auctionTime
    };
  }
  async swap(swapSnapshot) {
    const strategy = await this.getActiveStrategy();
    return await strategy.swap(swapSnapshot);
  }
  async getMaxAmount() {
    const snapshot = this.pairHolder.getSnapshot("source");
    const sourceToken = snapshot.token;
    const connectedWalletAddress = await this.walletController.data.getActiveAddress();
    if (!sourceToken || !connectedWalletAddress || !isAddress(sourceToken.address) || !isAddress(connectedWalletAddress)) return 0n;
    const chainId = sourceToken.chainId;
    if (!chainId) return 0n;
    try {
      const balance = await this.applicationContext.tokenController.getTokenBalance(chainId, sourceToken.address, connectedWalletAddress);
      let amount = BigInt(balance?.amount ?? 0);
      if (isNativeToken2(sourceToken.address)) {
        const chainId2 = await this.walletController.data.getChainId();
        if (!chainId2) return 0n;
        const [gasUnits, gasPriceDTO] = await Promise.all([
          estimateWrap(chainId2, amount),
          this.oneInchApiAdapter.getGasPrice(chainId2)
        ]);
        if (!gasPriceDTO) return 0n;
        const gasPrice = gasPriceDTO.high;
        const fee = gasUnits * (BigInt(gasPrice.maxFeePerGas) + BigInt(gasPrice.maxPriorityFeePerGas));
        amount = amount - fee;
        if (amount < 0n) {
          amount = 0n;
        }
      }
      return amount;
    } catch (error) {
      console.error("Error fetching max amount:", error);
      return 0n;
    }
  }
  async setMaxAmount() {
    const amount = await this.getMaxAmount();
    if (amount > 0n) {
      this.setTokenAmountByType("source", amount);
    }
  }
  getTokenByType(type) {
    return this.pairHolder.streamSnapshot(type).pipe(
      map2((snapshot) => snapshot.token),
      distinctUntilChanged2()
    );
  }
  getTokenAmountByType(type) {
    return this.pairHolder.streamSnapshot(type).pipe(
      map2((snapshot) => snapshot.amount),
      distinctUntilChanged2()
    );
  }
  getTokenRawAmountByType(type) {
    return this.pairHolder.streamSnapshot(type).pipe(
      map2((snapshot) => snapshot.amount),
      distinctUntilChanged2()
    );
  }
  setTokenAmountByType(type, value) {
    this.pairHolder.setAmount(type, value);
  }
  async getActiveStrategy() {
    const address = await this.walletController.data.getActiveAddress();
    if (address === null) {
      return this.contextStrategy.onChain;
    }
    return this.contextStrategy.fusion;
  }
  async getDataSnapshot(useOnChainStrategy) {
    if (!useOnChainStrategy) {
      try {
        return await this.contextStrategy.fusion.getDataSnapshot();
      } catch (error) {
        return this.getDataSnapshot(true);
      }
    }
    try {
      return await this.contextStrategy.onChain.getDataSnapshot();
    } catch (error) {
      return null;
    }
  }
};
function isTokenSnapshotNotNullable(snapshot) {
  return snapshot.token !== null && snapshot.amount !== null;
}

// node_modules/.pnpm/@lit+context@1.1.3/node_modules/@lit/context/lib/create-context.js
function n(n2) {
  return n2;
}

// libs/sdk/src/lib/swap/swap-context-token.ts
var SwapContextToken = n(Symbol("SwapContextToken"));

// libs/sdk/src/lib/swap/is-support-fusion.ts
import { ChainId as ChainId2 } from "@one-inch-community/models";
var isSupportFusionList = [
  ChainId2.eth,
  ChainId2.bnb,
  ChainId2.matic,
  ChainId2.arbitrum,
  ChainId2.avalanche,
  ChainId2.op,
  ChainId2.gnosis,
  ChainId2.fantom
];
function isSupportFusion(chainId) {
  return isSupportFusionList.includes(chainId);
}
export {
  SwapContext,
  SwapContextToken,
  isSupportFusion
};
//# sourceMappingURL=index.esm.js.map
